/**
 * O vínculo entre os componentes do projeto e o catálogo global de sistemas.
 *
 * São as três telas "Componentes x Sistemas", "x Nome Sistemas" e "x Fabricante
 * Sistemas" do menu antigo, que aqui viram uma só com três visões — porque são a
 * mesma pergunta feita contra catálogos diferentes.
 *
 * **Duas mecânicas diferentes por baixo.** O vínculo com *sistema* é um par
 * simples: componente + sistema. Os outros dois valem **por modelo**: o par
 * (componente, catálogo) só existe enquanto houver ao menos um modelo ligado a
 * ele, e some junto com o último. Quem apaga um modelo pode estar apagando o
 * vínculo inteiro, e a tela avisa isso antes.
 *
 * **Volume.** É pequeno e cabe inteiro na tela: 972 vínculos de sistema em toda
 * a produção, no máximo 31 num mesmo projeto. Nada de paginar aqui.
 *
 * A escrita é do projeto, não global — por isso `noProjeto(projetoId)`, e por
 * isso o canário protege estas telas normalmente.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

/** Um vínculo componente × sistema. */
export type VinculoDeSistema = {
  componenteId: number
  componenteCodigo: string
  componenteDescricao: string
  componenteApelido: string
  sistemaId: number
  sistemaNome: string
  pinout: boolean
}

/** Um modelo dentro de um vínculo por modelo. */
export type ModeloDoVinculo = {
  modeloId: number
  modeloDescricao: string
}

/**
 * Um vínculo componente × (nome de sistema | fabricante), com os modelos em que
 * vale. `semVinculoPai` marca a linha que ficou sem o registro-pai — existem 14
 * assim em produção, todas no projeto 42.
 */
export type VinculoPorModelo = {
  componenteId: number
  componenteCodigo: string
  componenteDescricao: string
  componenteApelido: string
  catalogoId: number
  catalogoNome: string
  modelos: ModeloDoVinculo[]
  semVinculoPai: boolean
}

/** Um sistema candidato, com quantas vezes já foi usado para o mesmo apelido. */
export type CandidatoDeSistema = {
  sistemaId: number
  sistemaNome: string
  ocorrencias: number
}

/**
 * Sugestão para um componente sem vínculo, tirada de componentes de mesmo
 * apelido em outros projetos. Vêm **todos** os candidatos, não um só: há
 * apelidos ligados a dez sistemas diferentes, e escolher por conta seria chutar.
 */
export type SugestaoDeSistema = {
  componenteId: number
  componenteCodigo: string
  componenteDescricao: string
  componenteApelido: string
  candidatos: CandidatoDeSistema[]
}

export type ResumoDosVinculos = {
  componentes: number
  componentesComSistema: number
  /** Sem apelido não há sugestão possível — a busca é por apelido. */
  componentesSemApelido: number
  vinculosDeSistema: number
  vinculosDeNome: number
  vinculosDeFabricante: number
}

const base = (projetoId: number) => `api/projetos/${projetoId}/vinculos-de-sistema`

export function usarResumoDosVinculos(projetoId: number) {
  return useQuery({
    queryKey: ["vinculos-resumo", projetoId] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<ResumoDosVinculos>(`${base(projetoId)}/resumo`, {}),
  })
}

export function usarVinculosDeSistema(projetoId: number) {
  return useQuery({
    queryKey: ["vinculos-sistemas", projetoId] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<VinculoDeSistema[]>(`${base(projetoId)}/sistemas`, {}),
  })
}

export function usarVinculosPorModelo(projetoId: number, tipo: "nomes" | "fabricantes") {
  return useQuery({
    queryKey: ["vinculos-por-modelo", projetoId, tipo] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<VinculoPorModelo[]>(`${base(projetoId)}/${tipo}`, {}),
  })
}

/** Só é buscada quando a pessoa abre o painel de sugestões — é a consulta mais cara das quatro. */
export function usarSugestoesDeSistema(projetoId: number, ligada: boolean) {
  return useQuery({
    queryKey: ["vinculos-sugestoes", projetoId] as const,
    enabled: projetoId > 0 && ligada,
    queryFn: () => obter<SugestaoDeSistema[]>(`${base(projetoId)}/sistemas/sugestoes`, {}),
  })
}

function invalidarTudo(qc: ReturnType<typeof useQueryClient>, projetoId: number) {
  qc.invalidateQueries({ queryKey: ["vinculos-sistemas", projetoId] })
  qc.invalidateQueries({ queryKey: ["vinculos-por-modelo", projetoId] })
  qc.invalidateQueries({ queryKey: ["vinculos-sugestoes", projetoId] })
  qc.invalidateQueries({ queryKey: ["vinculos-resumo", projetoId] })
}

export function usarLigarSistema(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ componenteId, sistemaId }: { componenteId: number; sistemaId: number }) => {
      // `pinout: true` é o que o legado sempre grava pela tela. A coluna aceita
      // false e há linhas assim no banco, mas nenhuma tela nunca gravou uma.
      await api.post(`${base(projetoId)}/sistemas`,
        { componenteId, sistemaId, pinout: true }, noProjeto(projetoId))
    },
    onSuccess: () => invalidarTudo(qc, projetoId),
  })
}

export function usarDesligarSistema(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ componenteId, sistemaId }: { componenteId: number; sistemaId: number }) => {
      await api.delete(`${base(projetoId)}/sistemas/${componenteId}/${sistemaId}`, noProjeto(projetoId))
    },
    onSuccess: () => invalidarTudo(qc, projetoId),
  })
}

export function usarLigarPorModelo(projetoId: number, tipo: "nomes" | "fabricantes") {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { componenteId, catalogoId, modeloIds }:
      { componenteId: number; catalogoId: number; modeloIds: number[] },
    ) => {
      await api.post(`${base(projetoId)}/${tipo}`,
        { componenteId, catalogoId, modeloIds }, noProjeto(projetoId))
    },
    onSuccess: () => invalidarTudo(qc, projetoId),
  })
}

export function usarDesligarPorModelo(projetoId: number, tipo: "nomes" | "fabricantes") {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      { componenteId, catalogoId, modeloId }:
      { componenteId: number; catalogoId: number; modeloId: number },
    ) => {
      await api.delete(
        `${base(projetoId)}/${tipo}/${componenteId}/${catalogoId}/${modeloId}`, noProjeto(projetoId))
    },
    onSuccess: () => invalidarTudo(qc, projetoId),
  })
}
